'use client';

import { Group } from '@visx/group';
import { useTooltip } from '@visx/tooltip';
import { useCallback, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { AnnotationList } from '@/components/Charts/modules/Annotations';
import { ChartTooltip } from '@/components/Charts/modules/ChartTooltip';
import Legend from '@/components/Charts/modules/Legend/index';
import { accessor } from '@/components/Charts/utils/accessor';
import { DASH_STYLES, DEFAULT_MARGINS } from '@/components/Charts/utils/chartDefaults';
import { formatTickDate } from '@/components/Charts/utils/formatTick';
import { getColorScales } from '@/components/Charts/utils/getScales';
import { getTimeTickInterval } from '@/components/Charts/XYCharts/utils/getTimeTickInterval';
import type { ChartValues, Data } from '@/types/chart';
import { cn } from '@/utils/ui-utils';
import { AxisBottom, AxisLeft, GridRows } from '../../../../ui/Blocks';
import { ChartBase } from '../../../../ui/ChartBase';
import { filterTicksByWidth } from '../../../utils/filterTicksByWidth';
import { getScales } from '../../../utils/getScales';
import Line from '../../modules/Line';
import { ReactiveArea } from '../../modules/ReactiveArea';
import type { LineChartValues } from '../../types/LineChartValues';
import { createChartHandlers } from '../../utils/handlers';

type TimeAxisBottomProps = {
  chartScales: ReturnType<typeof getScales>;
  innerWidth: number;
  innerHeight: number;
};

export function TimeAxisBottom({ chartScales, innerWidth, innerHeight }: TimeAxisBottomProps) {
  const rawTicks = chartScales.xScaleAxis.ticks(getTimeTickInterval(innerWidth));
  const ticks = filterTicksByWidth(rawTicks, innerWidth);

  return (
    <AxisBottom
      scale={chartScales.xScaleAxis}
      top={innerHeight}
      tickValues={ticks}
      tickFormat={(value) => formatTickDate(value)}
    />
  );
}

interface TimeChart {
  width: number;
  height: number;
  data: ChartValues[][];
  unit: string;
  showAnnotations: boolean;
}

const TimeChart = ({ width, height, data, unit, showAnnotations }: TimeChart) => {
  const tooltipProps = useTooltip<{ data: Data }>();

  const hasComparison = data.length > 1;
  const colorScales = getColorScales({
    numberOfColors: data[0].length,
    numberOfComparisons: data.length,
  });

  const [chartData, setChartData] = useState<LineChartValues[][]>(
    data.map((d, groupIndex) =>
      d.map((d, lineIndex) => ({
        ...d,
        id: uuid(),
        groupIndex: groupIndex,
        dashStyle: DASH_STYLES[lineIndex],
        color: {
          default: String(colorScales[groupIndex].default(lineIndex)),
          hover: String(colorScales[groupIndex].hover(lineIndex)),
        },
        visible: true,
        hovered: false,
      })),
    ),
  );

  const flatData = chartData.flat();
  const [circlePos, setCirclePos] = useState<{ x: number; y: number } | null>(null);

  const chartMargins = { ...DEFAULT_MARGINS };

  const innerWidth = width - chartMargins.right;
  const innerHeight = height - chartMargins.top - chartMargins.bottom;

  const chartScales = useMemo(
    () =>
      getScales({
        data: flatData,
        accessor,
        innerWidth,
        innerHeight,
      }),
    [flatData, innerWidth, innerHeight],
  );

  chartMargins.left = chartScales.tickLength;

  const visibleCount = useMemo(() => flatData.filter((d) => d.visible).length, [flatData]);

  const hovered = useMemo(() => flatData.find((d) => d.hovered), [flatData]);

  const chartHandlers = useCallback(
    () => createChartHandlers(setCirclePos, tooltipProps.showTooltip, chartScales, setChartData),
    [tooltipProps.showTooltip, chartScales],
  );

  const visibleLines = flatData.filter((d) => d.visible);
  const handlers = chartHandlers();
  return (
    <ChartBase
      height={height}
      width={width}
    >
      <ChartBase.Body>
        <Group
          left={chartMargins.left}
          top={chartMargins.top}
        >
          {/* Grid lines */}
          <GridRows
            scale={chartScales.yScale}
            width={innerWidth}
            height={innerHeight - chartMargins.top}
          />

          {/* Y-axis */}
          <AxisLeft scale={chartScales.yScale} />
          {visibleLines.map((line) => {
            return (
              <Line
                key={line.id}
                data={line}
                hovered={visibleCount === 1 ? line.id : hovered?.id}
                margin={chartMargins.left}
                accessor={accessor}
                chartColor={line.color}
                dashStyles={line.dashStyle}
                circlePos={circlePos}
                showGradient={!hasComparison}
                {...chartScales}
              />
            );
          })}
          <ReactiveArea
            height={height}
            width={width}
            chartMargins={chartMargins}
            onMouseMove={(event) => {
              const closestLine = handlers.getClosest(event, visibleLines);
              const { handleTooltip, handleCirclePost, handleHover } = handlers;
              handleTooltip(closestLine);
              handleCirclePost(closestLine);
              handleHover(closestLine);
            }}
            onMouseLeave={() => {
              const { hideTooltip } = tooltipProps;
              hideTooltip();
              setCirclePos(null);
              if (visibleLines.length > 1) {
                handlers.setHovered(null, null);
              }
            }}
          />

          {/* X-axis */}
          <TimeAxisBottom
            chartScales={chartScales}
            innerHeight={innerHeight}
            innerWidth={innerWidth}
          />
        </Group>
      </ChartBase.Body>
      <ChartBase.Legend>
        <ChartLegend
          chartData={chartData}
          chartHandlers={handlers}
          visibleCount={visibleCount}
        />
      </ChartBase.Legend>
      <ChartBase.Annotations>
        <AnnotationList
          chartValues={chartData.flatMap((d) => d.flatMap((d) => d.data.flatMap((d) => d.value)))}
          chartMargins={chartMargins}
          chartScales={chartScales}
          showAnnotations={showAnnotations}
          unit={unit}
        />
      </ChartBase.Annotations>
      <ChartBase.Tooltip>
        <ChartTooltip
          chartMargins={chartMargins}
          innerHeight={innerHeight}
          accessor={accessor}
          unit={unit}
          {...tooltipProps}
        />
      </ChartBase.Tooltip>
    </ChartBase>
  );
};

const ChartLegend = ({
  chartData,
  chartHandlers,
  visibleCount,
}: {
  chartData: LineChartValues[][];
  chartHandlers: ReturnType<typeof createChartHandlers>;
  visibleCount: number;
}) => {
  const { handleLegendClick, setHovered } = chartHandlers;
  return (
    <div className={cn('mt-1 flex h-full flex-wrap gap-4')}>
      {chartData.map((data, groupIndex) => (
        <div key={data[0]?.id ?? `group-${groupIndex}`}>
          <ul className='flex w-full flex-wrap gap-2'>
            {data.map((d, index) => {
              const noData = d.data.length === 0;
              return (
                <Legend
                  key={`legend-${d.id}`}
                  data={d}
                  disabled={noData}
                  onClick={() => handleLegendClick(index, d.groupIndex)}
                  onMouseEnter={() => setHovered(d.id, d.groupIndex)}
                  onMouseLeave={() => setHovered(null, d.groupIndex)}
                  dashStyle={d.dashStyle}
                  visibleCount={visibleCount}
                />
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
};

export { TimeChart };
